import { motion } from "framer-motion";

const AXES = [
  { key: "PHY", short: "PHYSICAL", name: "Physical Wellness" },
  { key: "ENV", short: "ENVIRON", name: "Environmental Wellness" },
  { key: "EMO", short: "EMOTIONAL", name: "Emotional Wellness" },
  { key: "SPI", short: "SPIRITUAL", name: "Spiritual Wellness" },
];

const LABEL_STYLE = {
  Thriving: "bg-gold/15 text-gold-dark",
  "Finding Balance": "bg-sage text-forest",
  "Out of Balance": "bg-[#F1E9D8] text-[#7A6A45]",
  "Survival Mode": "bg-forest text-ivory",
  "Insufficient Information": "bg-charcoal/5 text-taupe",
};

export const WellnessSnapshot = ({ dimensions }) => {
  const size = 340;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 58;
  const ang = (i) => (2 * Math.PI * i) / 4 - Math.PI / 2;
  const pt = (i, v) => [cx + Math.cos(ang(i)) * r * (v / 3), cy + Math.sin(ang(i)) * r * (v / 3)];
  const ring = (v) => [0, 1, 2, 3].map((i) => pt(i, v).join(",")).join(" ");
  const idxOf = (k) => dimensions?.[k]?.index;
  const dataPts = AXES.map((a, i) => pt(i, Math.max(idxOf(a.key) ?? 0, 0.06)).join(",")).join(" ");
  const summaryText = AXES.map(
    (a) => `${a.name}: ${dimensions?.[a.key]?.label || "not enough answers yet"}`
  ).join(". ");

  return (
    <div data-testid="results-snapshot">
      <div className="relative mx-auto max-w-[340px]">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="h-auto w-full"
          role="img"
          aria-label={`Wellness snapshot. ${summaryText}`}
        >
          {[1, 2, 3].map((v) => (
            <polygon
              key={v}
              points={ring(v)}
              fill={v === 3 ? "#E5ECE3" : "none"}
              fillOpacity={v === 3 ? 0.35 : 0}
              stroke="#173F35"
              strokeOpacity={v === 3 ? 0.22 : 0.09}
            />
          ))}
          {[0, 1, 2, 3].map((i) => {
            const [x, y] = pt(i, 3);
            return (
              <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#173F35" strokeOpacity="0.12" />
            );
          })}
          <motion.polygon
            points={dataPts}
            fill="#B49253"
            fillOpacity="0.22"
            stroke="#B49253"
            strokeWidth="2"
            strokeLinejoin="round"
            initial={{ opacity: 0, scale: 0.65 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: `${cx}px ${cy}px` }}
          />
          {AXES.map((a, i) => {
            const idx = idxOf(a.key);
            const [x, y] = pt(i, Math.max(idx ?? 0, 0.06));
            return (
              <motion.circle
                key={a.key}
                cx={x}
                cy={y}
                r="5"
                fill={idx == null ? "#FAF8F2" : "#173F35"}
                stroke="#173F35"
                strokeWidth={idx == null ? 1.5 : 0}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + i * 0.08 }}
              />
            );
          })}
          {AXES.map((a, i) => {
            const [x, y] = pt(i, 3.75);
            return (
              <text
                key={a.key}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="11"
                fontWeight="700"
                letterSpacing="1.6"
                fill="#173F35"
              >
                {a.short}
              </text>
            );
          })}
        </svg>
      </div>
      <ul className="sr-only">
        {AXES.map((a) => (
          <li key={a.key}>
            {a.name}: {dimensions?.[a.key]?.label || "not enough answers yet"}
          </li>
        ))}
      </ul>
      <div className="mt-6 grid grid-cols-2 gap-2.5">
        {AXES.map((a) => (
          <div
            key={a.key}
            data-testid={`snapshot-legend-${a.key.toLowerCase()}`}
            className="rounded-2xl border border-forest/10 bg-white/70 px-4 py-3 text-center"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-taupe">
              {a.name}
            </p>
            <span
              className={`mt-1.5 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${LABEL_STYLE[dimensions?.[a.key]?.label] || LABEL_STYLE["Insufficient Information"]}`}
            >
              {dimensions?.[a.key]?.label || "Not enough answers"}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-xs leading-relaxed text-taupe">
        Each point reflects your self-report support index for that dimension, on a scale of 0
        to 3 based on your own answers. It describes what you reported, never a medical
        measurement or a health percentage. Inheritance & Personal History is shown separately
        as context, never as a number.
      </p>
    </div>
  );
};
